import { useState, useCallback } from 'react';

/**
 * Hook to wrap any API call with 2FA protection.
 *
 * Usage:
 *   const { protect, show2fa, error2fa, handleVerify, handleClose } = use2faProtect();
 *
 *   // Wrap your API call:
 *   const handleAction = () => protect(async (headers) => {
 *     await adminAPI.updateUserRole(userId, data, headers);
 *   });
 *
 *   // Render the TwoFactorModal in your JSX
 *
 * When the API returns 2fa_required, the modal opens automatically.
 * After the user enters the code, the original call is retried with X-2FA-Code.
 * Handles 2fa_invalid (wrong code) and 2fa_rate_limited (too many attempts).
 */
export default function use2faProtect() {
  const [show2fa, setShow2fa] = useState(false);
  const [error2fa, setError2fa] = useState('');
  const [pendingAction, setPendingAction] = useState(null);

  /**
   * Wrap an async function with 2FA protection.
   * The function receives an optional headers object to pass to axios.
   */
  const protect = useCallback(async (fn) => {
    try {
      setError2fa('');
      await fn({});
    } catch (err) {
      const code = err.response?.data?.code;
      if (code === '2fa_required') {
        setPendingAction(() => fn);
        setShow2fa(true);
        setError2fa('');
      } else if (code === '2fa_invalid') {
        if (show2fa) {
          setError2fa(err.response?.data?.message || 'Invalid code. Please try again.');
        } else {
          throw err;
        }
      } else if (code === '2fa_rate_limited') {
        setError2fa(err.response?.data?.message || 'Too many failed attempts. Try again later.');
        // Keep modal open but show rate limit message
        if (!show2fa) {
          setPendingAction(() => fn);
          setShow2fa(true);
        }
      } else {
        throw err;
      }
    }
  }, [show2fa]);

  const handleVerify = useCallback(async (totpCode) => {
    if (!pendingAction) return;
    try {
      setError2fa('');
      await pendingAction({ 'X-2FA-Code': totpCode });
      // Success — close modal
      setShow2fa(false);
      setPendingAction(null);
    } catch (err) {
      const code = err.response?.data?.code;
      if (code === '2fa_invalid' || code === '2fa_rate_limited') {
        setError2fa(err.response?.data?.message || 'Verification failed.');
      } else {
        setShow2fa(false);
        setPendingAction(null);
        throw err;
      }
    }
  }, [pendingAction]);

  const handleClose = useCallback(() => {
    setShow2fa(false);
    setPendingAction(null);
    setError2fa('');
  }, []);

  return {
    protect,
    show2fa,
    error2fa,
    handleVerify,
    handleClose,
  };
}
